/**
 * Chapter014.js — Workplace Inspection Checklist (OHSE 014)
 * Migrated from SWMS Platform Chapter014.tsx → JavaScript
 *
 * Props:
 *   data        {array}   workplace_inspections from swmsData
 *   onChange    {fn}      (newInspections) => void
 *   companyName {string}  from companyInfo.name
 *   projectName {string}  from projectInfo.project_name (pre-fills Workplace field)
 */

import { useState, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';

// ── Default inspection categories (embedded from SWMS types) ──────────────────
function defaultCategories() {
  return [
    {
      id: crypto.randomUUID(), name: 'Access/Egress',
      items: [
        { id: crypto.randomUUID(), description: 'Access paths clear', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Access paths defined (signage tape, other)', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Prohibited areas display warning signs and barricaded', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Dust/Air Quality',
      items: [
        { id: crypto.randomUUID(), description: 'Dust suppressed/watered down', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Stock piles protected from wind', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Plant & equipment maintained to minimise emissions', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Electrical',
      items: [
        { id: crypto.randomUUID(), description: 'Electrical equipment tested & tagged', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Register of tagging current', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Portable generator fitted RCD', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Portable Residual Current Device (RCD) tested/tagged', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'First Aid/Emergency/Injury',
      items: [
        { id: crypto.randomUUID(), description: 'First aid kit provided', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Kit stocks refreshed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'First Aid Officer available', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Evacuation procedure in place', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Emergency contacts displayed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Fire extinguisher/equipment available', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Manual Handling',
      items: [
        { id: crypto.randomUUID(), description: 'Trolleys/aids in use', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'SWMS followed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Training/job rotation undertaken', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Hazardous Substances/Dangerous Goods',
      items: [
        { id: crypto.randomUUID(), description: 'Register current', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'MSDS available', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'SWMS lists precautions for use', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Storage area bunded', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Refuelling SWMS followed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Height Work',
      items: [
        { id: crypto.randomUUID(), description: 'Perimeter protection', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Handrails in place', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Penetrations covered', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Fall restraint/arrest system in use', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'SWMS followed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Housekeeping',
      items: [
        { id: crypto.randomUUID(), description: 'Materials stacked', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Work area lit', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Bins available & in use', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Signage in place', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Leads suspended', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Walkway/stairs/work area clear', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Noise',
      items: [
        { id: crypto.randomUUID(), description: 'Plant & equipment maintained', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Site hours observed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Noisy works identified', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Hearing protection used (SWMS)', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Personal Protective Equipment',
      items: [
        { id: crypto.randomUUID(), description: 'Used when required (SWMS)', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Correctly used by employees', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Plant & Equipment',
      items: [
        { id: crypto.randomUUID(), description: 'Plant register current', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Maintenance records provided', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Daily log book completed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Operator ticketed/competency verified', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'SWMS followed', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Public Protection',
      items: [
        { id: crypto.randomUUID(), description: 'Work area secure from public', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Overhead protection provided', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Stormwater/Run Off',
      items: [
        { id: crypto.randomUUID(), description: 'Silt control fences in place', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Stormwater inlets protected', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Discharges contained, e.g. pump out, slurry/other', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Training',
      items: [
        { id: crypto.randomUUID(), description: 'General industry (safety awareness) training', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Site specific induction training', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Work activity (SWMS) training', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Vegetation',
      items: [
        { id: crypto.randomUUID(), description: 'Fencing around drip line of retained trees', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'No material/equipment stored within drip line', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
    {
      id: crypto.randomUUID(), name: 'Waste Management',
      items: [
        { id: crypto.randomUUID(), description: 'Waste reduction plan in place', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Waste contractor records available', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Bins for litter/cigarette butts/other provided', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
        { id: crypto.randomUUID(), description: 'Hazardous wastes captured & correct disposal', status: '', priority: '', action_by: '', close_out_by: '', close_out_date: '' },
      ],
    },
  ];
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Chapter014({ data = [], onChange, companyName, projectName }) {
  const cn = companyName || '[Company Name]';

  const addInspection = useCallback(() => {
    onChange([...data, {
      id: crypto.randomUUID(),
      workplace: projectName || '',
      date: '',
      inspected_by: '',
      signature: '',
      categories: defaultCategories(),
    }]);
  }, [data, onChange, projectName]);

  const updateInspection = useCallback((idx, updated) => {
    const next = [...data];
    next[idx] = updated;
    onChange(next);
  }, [data, onChange]);

  const deleteInspection = useCallback((idx) => {
    onChange(data.filter((_, i) => i !== idx));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 014</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Workplace Inspection Checklist
        </h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        inspects the work activity(s) and work area, and provides a completed Workplace Inspection
        Checklist each week to the principal contractor for the duration of the works.
      </div>

      {/* Inspection records */}
      {data.map((inspection, idx) => (
        <InspectionCard
          key={inspection.id || idx}
          inspection={inspection}
          index={idx}
          onChange={(updated) => updateInspection(idx, updated)}
          onDelete={() => deleteInspection(idx)}
        />
      ))}

      {/* Add button */}
      <div className="flex justify-center">
        <button type="button" onClick={addInspection}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
          <Plus className="w-4 h-4" /> Add Inspection
        </button>
      </div>
    </div>
  );
}

// ── Inspection Card ────────────────────────────────────────────────────────────

function InspectionCard({ inspection, index, onChange, onDelete }) {
  const updateCat = (catIdx, updated) => {
    const next = [...inspection.categories];
    next[catIdx] = updated;
    onChange({ ...inspection, categories: next });
  };

  const updateItem = (catIdx, itemIdx, updated) => {
    const cat = inspection.categories[catIdx];
    const items = [...cat.items];
    items[itemIdx] = updated;
    updateCat(catIdx, { ...cat, items });
  };

  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold shrink-0">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Workplace Inspection
          </span>
        </div>
        <button type="button" onClick={onDelete} className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Workplace">
            <input type="text" value={inspection.workplace || ''} placeholder="e.g. Site location"
              onChange={e => onChange({ ...inspection, workplace: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Date">
            <input type="date" value={inspection.date || ''}
              onChange={e => onChange({ ...inspection, date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Inspected By">
            <input type="text" value={inspection.inspected_by || ''} placeholder="Name of inspector"
              onChange={e => onChange({ ...inspection, inspected_by: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Signature">
            <input type="text" value={inspection.signature || ''} placeholder="Signature (type name)"
              onChange={e => onChange({ ...inspection, signature: e.target.value })} className={inputCls} />
          </Field>
        </div>

        {/* Checklist Categories */}
        <div className="border-t border-[#e0dbd0] pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Inspection Checklist</p>

          {/* Table header */}
          <div className="grid grid-cols-[1fr_80px_70px_110px_110px_100px] gap-2 px-3 py-2 bg-[#1a1a2e]/5 rounded-t-lg text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
            <span>Item</span>
            <span>Status</span>
            <span>Priority</span>
            <span>Action By</span>
            <span>Close Out By</span>
            <span>Close Out Date</span>
          </div>

          {inspection.categories.map((cat, catIdx) => (
            <CategorySection
              key={cat.id || catIdx}
              category={cat}
              catIdx={catIdx}
              onUpdateItem={updateItem}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Category Section (collapsible) ─────────────────────────────────────────────

function CategorySection({ category, catIdx, onUpdateItem }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-[#e0dbd0] rounded-lg overflow-hidden mt-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#f8f7f4] hover:bg-[#f0ede6] transition-colors text-left"
      >
        {expanded
          ? <ChevronDown className="w-4 h-4 text-[#c9a84c] shrink-0" />
          : <ChevronRight className="w-4 h-4 text-[#c9a84c] shrink-0" />
        }
        <span className="text-sm font-semibold text-[#1a1a2e]">{category.name}</span>
        <span className="ml-auto text-xs text-gray-400">{category.items.length} items</span>
      </button>

      {expanded && (
        <div className="divide-y divide-[#e0dbd0]/50">
          {category.items.map((item, itemIdx) => (
            <div
              key={item.id || itemIdx}
              className="grid grid-cols-[1fr_80px_70px_110px_110px_100px] gap-2 px-3 py-2 items-center hover:bg-[#f8f7f4]/50 transition-colors"
            >
              <span className="text-sm text-[#1a1a2e]">{item.description}</span>

              <select
                value={item.status || ''}
                onChange={e => onUpdateItem(catIdx, itemIdx, { ...item, status: e.target.value })}
                className="h-8 text-xs border border-[#e0dbd0] rounded px-1.5 bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]"
              >
                <option value="">—</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="n/a">N/A</option>
              </select>

              <select
                value={item.priority || ''}
                onChange={e => onUpdateItem(catIdx, itemIdx, { ...item, priority: e.target.value })}
                className="h-8 text-xs border border-[#e0dbd0] rounded px-1.5 bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]"
              >
                <option value="">—</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>

              <input type="text" value={item.action_by || ''} placeholder="Name"
                onChange={e => onUpdateItem(catIdx, itemIdx, { ...item, action_by: e.target.value })}
                className="h-8 text-xs px-2 border border-transparent rounded hover:border-[#e0dbd0] focus:border-[#c9a84c] focus:outline-none focus:bg-white" />

              <input type="text" value={item.close_out_by || ''} placeholder="Name"
                onChange={e => onUpdateItem(catIdx, itemIdx, { ...item, close_out_by: e.target.value })}
                className="h-8 text-xs px-2 border border-transparent rounded hover:border-[#e0dbd0] focus:border-[#c9a84c] focus:outline-none focus:bg-white" />

              <input type="date" value={item.close_out_date || ''}
                onChange={e => onUpdateItem(catIdx, itemIdx, { ...item, close_out_date: e.target.value })}
                className="h-8 text-xs px-1 border border-transparent rounded hover:border-[#e0dbd0] focus:border-[#c9a84c] focus:outline-none focus:bg-white" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
